/**
 * Tenant-Scoped Authentication Store
 * 
 * This store manages authentication state with tenant isolation.
 * It uses the tenantAuthService for all authentication operations.
 * 
 * Key Features:
 * - Tenant-scoped login (requires tenant slug)
 * - User data includes tenant and role information
 * - Automatic session management
 * - Role-based access control helpers
 */

import { create } from 'zustand';
import { authService, UserWithTenant, AuthError, TENANT_DEACTIVATED_CODE, USER_DEACTIVATED_CODE } from '../services/auth';

/** A deactivated organisation or user account is an expected refusal, not an error worth reporting. */
const isTenantDeactivated = (error: unknown): error is AuthError =>
  error instanceof AuthError && (error.code === TENANT_DEACTIVATED_CODE || error.code === USER_DEACTIVATED_CODE);
import { setUserContext, captureException, addBreadcrumb } from '../lib/sentry';
import { queryClient } from '../lib/queryClient';

interface TenantAuthState {
  user: UserWithTenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  showLoginModal: boolean;

  // Actions
  loginWithTenant: (email: string, password: string, tenantSlug: string) => Promise<UserWithTenant>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setShowLoginModal: (show: boolean) => void;
  handleSessionExpired: () => void;
  clearError: () => void;

  // Helper methods
  hasRole: (roleName: string) => boolean;
  hasAnyRole: (roleNames: string[]) => boolean;
  isAdmin: () => boolean;
  getTenantId: () => string | null;
  getTenantSlug: () => string | null;
}

export const useTenantAuthStore = create<TenantAuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start as loading to prevent premature redirects
  error: null,
  showLoginModal: false,

  /**
   * Login with tenant context
   * Validates that user belongs to the specified tenant
   */
  loginWithTenant: async (email: string, password: string, tenantSlug: string) => {
    set({ isLoading: true, error: null });

    addBreadcrumb('Login attempt', 'auth', 'info', { tenantSlug });

    try {
      const { user } = await authService.signInWithTenant(email, password, tenantSlug);

      // Set user context in Sentry
      setUserContext({
        id: user.id,
        email: user.email,
        tenantId: user.tenant?.id,
        roles: user.roles,
      });

      addBreadcrumb('Login successful', 'auth', 'info', { userId: user.id });

      // Set authenticated state immediately with user data
      // This prevents race condition with onAuthStateChange SIGNED_IN event
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        showLoginModal: false,
        error: null
      });

      // Return the user data for immediate use (e.g., navigation)
      return user;
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed';

      // Only report unexpected auth errors (not wrong password, etc.)
      if (error instanceof Error && !errorMessage.includes('does not belong to tenant') && !isTenantDeactivated(error)) {
        captureException(error, {
          tags: {
            errorType: 'auth_error',
            operation: 'login',
          },
          extra: {
            tenantSlug,
            errorMessage,
          },
          level: 'warning',
        });
      }

      addBreadcrumb('Login failed', 'auth', 'error', { errorMessage });

      set({
        error: errorMessage,
        isLoading: false,
        isAuthenticated: false,
        isLoginInProgress: false,
        user: null
      });
      throw error;
    }
  },

  /**
   * Logout current user
   */
  logout: async () => {
    console.log('[TenantAuthStore] Logout called');
    addBreadcrumb('Logout initiated', 'auth', 'info');

    try {
      console.log('[TenantAuthStore] Calling authService.signOut()...');
      await authService.signOut();
      console.log('[TenantAuthStore] authService.signOut() completed');

      addBreadcrumb('Logout successful', 'auth', 'info');
    } catch (error) {
      console.error('[TenantAuthStore] Logout error:', error);

      // Report logout failures (shouldn't happen)
      captureException(error as Error, {
        tags: {
          errorType: 'auth_error',
          operation: 'logout',
        },
        level: 'warning',
      });
    } finally {
      console.log('[TenantAuthStore] Setting state to logged out');

      // Clear user context in Sentry
      setUserContext(null);

      // Drop every cached server response, so the next person to sign in on this browser never
      // sees the previous user's data (knowledge bases, users, analytics…).
      queryClient.clear();

      set({
        user: null,
        isAuthenticated: false,
        showLoginModal: false,
        error: null
      });
      console.log('[TenantAuthStore] State updated, logout complete');
    }
  },

  /**
   * Check authentication status and load user data
   */
  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const user = await authService.getCurrentUserWithTenant();

      if (user) {
        // Set user context in Sentry
        setUserContext({
          id: user.id,
          email: user.email,
          tenantId: user.tenant?.id,
          roles: user.roles,
        });

        console.log('[checkAuth] User authenticated:', user.email);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
      } else {
        // No session found - user is not authenticated (this is normal on first load)
        console.log('[checkAuth] No active session found');
        setUserContext(null);

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    } catch (error) {
      if (isTenantDeactivated(error)) {
        setUserContext(null);
        set({ user: null, isAuthenticated: false, isLoading: false, error: error.message });
        return;
      }

      console.error('[checkAuth] Failed with error:', error);

      // Report auth check failures (unexpected database errors)
      captureException(error as Error, {
        tags: {
          errorType: 'auth_error',
          operation: 'checkAuth',
        },
        level: 'warning',
      });

      setUserContext(null);

      // On error, treat as not authenticated but log it
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to load user profile. Please try logging in again.'
      });
    }
  },

  /**
   * Show/hide login modal
   */
  setShowLoginModal: (show: boolean) => {
    set({ showLoginModal: show, error: null });
  },

  /**
   * Handle session expiration
   */
  handleSessionExpired: () => {
    set({
      user: null,
      isAuthenticated: false,
      showLoginModal: true,
      error: 'Your session has expired. Please log in again.'
    });
  },

  /**
   * Clear error message
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Check if user has a specific role
   */
  hasRole: (roleName: string): boolean => {
    const { user } = get();
    return user?.roles?.includes(roleName) || false;
  },

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole: (roleNames: string[]): boolean => {
    const { user } = get();
    if (!user?.roles) return false;
    return roleNames.some(role => user.roles.includes(role));
  },

  /**
   * Check if user is an admin (super_admin or client_admin)
   */
  isAdmin: (): boolean => {
    const { hasAnyRole } = get();
    return hasAnyRole(['super_admin', 'client_admin']);
  },

  /**
   * Get current user's tenant ID
   */
  getTenantId: (): string | null => {
    const { user } = get();
    return user?.tenant?.id || null;
  },

  /**
   * Get current user's tenant slug
   */
  getTenantSlug: (): string | null => {
    const { user } = get();
    return user?.tenant?.slug || null;
  },
}));

// NOTE: Do NOT initialize auth state here on module load
// App.tsx handles auth initialization in useEffect to avoid duplicate calls

